import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { StatusBadgeComponent } from '../status-badge.component';

/**
 * Wrapper that paints the "Flag - active" state of the onboarding review (Figma States
 * 4082:159705, 3 sep export): amber surface (Background/default/Warning), 3px left border
 * (Border/Warning), radius 0/8/8/0, padding 16, gap 8, and a reason input under the content.
 * Not flagged = renders the projected content untouched (no chrome, no padding).
 *
 * The reason field follows Figma 1523:154111: a 14px label, a 120-character cap, a live
 * character counter and a line naming who reads the text. The cap is real (`maxlength`), not
 * advisory: the reason is quoted verbatim into the carrier's denial message, where a runaway
 * paragraph would bury the other findings.
 *
 * Resolved = the carrier has resubmitted on this marking. The chrome stays (the reviewer still
 * needs to see what he asked for), but in the neutral/subdued role instead of the warning one,
 * with an "Opnieuw ingediend" badge: nothing is being asked any more until the reviewer reopens it.
 */
@Component({
  selector: 'dd-review-flag-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StatusBadgeComponent],
  host: {
    '[class.is-flagged]': 'flagged()',
    '[class.is-resolved]': 'resolved()',
    '[attr.data-cy]': 'dataCy()',
  },
  template: `
    @if (resolved()) {
      <app-status-badge
        class="self-start"
        label="Opnieuw ingediend"
        variant="neutral"
        icon="heroicons_outline:arrow-path"
        [attr.data-cy]="dataCy() ? dataCy() + '-resolved-badge' : null" />
    }
    <ng-content />
    @if (flagged() || resolved()) {
      <label class="mt-2 flex flex-col gap-1">
        <span class="text-[0.875rem] font-medium leading-[1.4] text-default">Reden voor markering</span>
        <textarea
          class="w-full rounded-lg border border-[color:var(--dd-field-border)] bg-[color:var(--dd-field-bg)]
                 px-3 py-2 text-[0.875rem] text-default"
          rows="2"
          maxlength="120"
          [value]="reason()"
          [disabled]="disabled()"
          placeholder="Wat moet de vervoerder aanpassen?"
          [attr.data-cy]="dataCy() ? dataCy() + '-reason' : null"
          (input)="draft.set($any($event.target).value)"
          (change)="onChange($event)"></textarea>
        <span
          class="text-[0.75rem] leading-normal text-[color:var(--dd-card-muted-fg)] tabular-nums"
          [attr.data-cy]="dataCy() ? dataCy() + '-counter' : null">
          {{ length() }}/120 tekens
        </span>
      </label>
      <p class="m-0 text-[0.875rem] leading-[1.4] text-[color:var(--dd-card-muted-fg)]">
        @if (resolved()) {
          {{ staleHint() }}
        } @else {
          {{ visibilityHint() }}
        }
      </p>
    }
  `,
  styles: `
    /* display lives here, not in the host class binding — Tailwind's .block utility is
       !important in this repo (tailwind.config.js: important: true), which would beat the
       non-important ":host.is-flagged { display: flex }" below and silently keep the host
       block while flagged, killing the flex layout (and its gap) entirely. */
    :host {
      display: block;
    }

    :host.is-flagged,
    :host.is-resolved {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem;
      border-left: 3px solid var(--dd-border-warning);
      border-radius: 0 0.5rem 0.5rem 0;
      background-color: var(--dd-badge-warning-bg);
    }

    /* Resolved wins over flagged: the two are mutually exclusive per flag, but a host binding both
       must land on the muted role, never the amber one. Tokens only (light on :root, dark under
       .dark) — a hardcoded grey here would go invisible in dark mode. */
    :host.is-resolved {
      border-left-color: var(--dd-card-border-strong);
      background-color: var(--dd-surface-subdued);
      color: var(--dd-card-muted-fg);
    }
  `,
})
export class ReviewFlagPanelComponent {
  readonly flagged = input(false);
  /** The carrier resubmitted on this marking: shown muted with its reason, counted nowhere. */
  readonly resolved = input(false);
  readonly reason = input('');
  readonly disabled = input(false);
  readonly dataCy = input<string | null>(null);
  /**
   * Footnote shown once the carrier has resubmitted on this marking. Onboarding/carrier-specific
   * wording, so callers outside that flow can override it instead of this shared presentational
   * component baking in one caller's copy.
   */
  readonly staleHint = input('De vervoerder heeft opnieuw ingediend — deze markering telt niet meer mee');
  /** Footnote shown while the marking is still open. See {@link staleHint}. */
  readonly visibilityHint = input('Dit is zichtbaar voor de gebruiker bij het afkeuren van de onboarding');
  readonly reasonChange = output<string>();

  /**
   * What the counter reads. Seeded from `reason()` and re-seeded whenever the host pushes a new
   * one, but writable so the count follows the keystrokes — the reason itself is still only
   * committed on `change` (which a textarea also fires on blur), which is what the host's
   * debounced persist path expects.
   */
  protected readonly draft = linkedSignal(() => this.reason());
  protected readonly length = computed(() => this.draft().length);

  /**
   * Commit on `change` only. A textarea fires `change` when it loses focus after an edit, so a
   * blur handler next to it reported the SAME reason a second time: the host's `reason` input has
   * not been re-applied between the two handlers, so the guard below still compares against the
   * pre-edit value and lets the duplicate through. That second emission was a second PATCH,
   * racing the first.
   */
  onChange(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value !== this.reason()) this.reasonChange.emit(value);
  }
}
